import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CalendarDays, Hash, Image as ImageIcon, MoveLeft, Save } from "lucide-react";
import Sidebar from "@/pages/home/components/Sidebar";
import { noteDetails } from "@/mocks/noteDetail";
import { getNoteDoc, upsertNoteDoc, type NoteDoc } from "../note-store";
import DOMPurify from "dompurify";

export default function NoteEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const seed: NoteDoc | null = useMemo(() => (id ? getNoteDoc(id) : null), [id]);
  const fallback = noteDetails[0];
  const [title, setTitle] = useState(seed?.title ?? fallback.title);
  const [subtitle, setSubtitle] = useState(seed?.subtitle ?? fallback.subtitle);
  const [contentHtml, setContentHtml] = useState<string>(() => {
    if (!seed) return "";
    if (seed.format === "html") return seed.content || "";
    // markdown -> html (lightweight)
    return blocksToHtml(markdownBlocks(seed.content || ""));
  });
  const [tags, setTags] = useState((seed?.tags ?? fallback.tags).join(", "));
  const editorRef = useRef<HTMLDivElement | null>(null);
  const imgInputRef = useRef<HTMLInputElement | null>(null);

  const stats = useMemo(() => {
    const txt = stripHtml(contentHtml).trim();
    const words = txt ? txt.replace(/\s/g, "").length : 0; // 中文/英文统一按非空字符估算
    const paras = txt ? txt.split(/\n{2,}/).filter(Boolean).length : 0;
    const headings = countHeadingsFromHtml(contentHtml);
    return { words, paras, headings };
  }, [contentHtml]);

  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    // keep DOM in sync with state (initial mount)
    el.innerHTML = contentHtml || "";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = () => {
    if (!id) return;
    const updatedAt = new Date().toISOString().slice(0, 10);
    const sanitized = DOMPurify.sanitize(contentHtml || "", {
      USE_PROFILES: { html: true },
      ALLOWED_TAGS: ["p", "br", "strong", "em", "u", "s", "a", "img", "h2", "h3", "ul", "ol", "li", "blockquote", "code", "pre", "span", "div"],
      ALLOWED_ATTR: ["href", "target", "rel", "src", "alt", "title", "style"],
    });
    upsertNoteDoc({
      id,
      title: title.trim() || "未命名笔记",
      subtitle: subtitle.trim(),
      content: sanitized,
      format: "html",
      tags: tags
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean),
      updatedAt,
    });
    navigate(`/knowledge/note/${id}`);
  };

  const syncFromDom = () => {
    const el = editorRef.current;
    if (!el) return;
    setContentHtml(el.innerHTML);
  };

  const handlePickImage = async (file: File | null) => {
    if (!file) return;
    const ok = /^image\//.test(file.type);
    if (!ok) return;
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ""));
      reader.onerror = () => reject(new Error("read image failed"));
      reader.readAsDataURL(file);
    });
    const safeName = file.name.replace(/\s+/g, "-");
    const el = editorRef.current;
    if (!el) return;
    el.focus();
    const img = document.createElement("img");
    img.src = dataUrl;
    img.alt = safeName;
    img.style.maxWidth = "100%";
    img.style.borderRadius = "12px";
    img.style.border = "1px solid #E5E7EB";
    img.style.display = "block";
    img.style.margin = "12px 0";

    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      const range = sel.getRangeAt(0);
      range.deleteContents();
      range.insertNode(img);
      // insert a paragraph after image to continue typing
      const p = document.createElement("p");
      p.appendChild(document.createElement("br"));
      img.after(p);
      const nextRange = document.createRange();
      nextRange.setStart(p, 0);
      nextRange.collapse(true);
      sel.removeAllRanges();
      sel.addRange(nextRange);
    } else {
      el.appendChild(img);
    }
    syncFromDom();
    if (imgInputRef.current) imgInputRef.current.value = "";
  };

  return (
    <div className="flex h-screen overflow-hidden bg-white text-foreground">
      <Sidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />

      <main className="flex min-w-0 flex-1 flex-col overflow-hidden bg-white">
        <div className="border-b border-[#E5E7EB] bg-white">
          <div className="mx-auto flex h-16 w-full max-w-[1100px] items-center gap-3 px-0">
            <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 rounded-lg border border-[#E5E7EB] bg-white px-3.5 py-2 text-sm font-medium text-[#111827] transition-colors hover:bg-[#F9FAFB]">
              <ArrowLeft className="h-4 w-4" /> 返回
            </button>
            <span className="text-[#6B7280]">/</span>
            <h1 className="truncate text-base font-semibold tracking-tight text-[#111827]">编辑笔记</h1>
            <div className="ml-auto flex items-center gap-2">
              <button onClick={() => navigate(`/knowledge/note/${id}`)} className="inline-flex items-center gap-2 rounded-lg border border-[#E5E7EB] bg-white px-3.5 py-2 text-sm font-medium text-[#111827] transition-colors hover:bg-[#F9FAFB]">
                <MoveLeft className="h-4 w-4" /> 预览
              </button>
              <button onClick={handleSave} className="inline-flex items-center gap-2 rounded-lg bg-[#111827] px-3.5 py-2 text-sm font-medium text-white transition-colors hover:bg-black">
                <Save className="h-4 w-4" /> 保存
              </button>
            </div>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="mx-auto grid w-full max-w-[1100px] gap-8 py-8 lg:grid-cols-[minmax(0,1fr)_280px]">
            <section className="space-y-8">
              <div className="space-y-5">
                <div>
                  <label className="mb-2 block text-sm font-medium text-[#111827]">标题</label>
                  <input value={title} onChange={(e) => setTitle(e.target.value)} className="w-full rounded-xl border border-[#E5E7EB] bg-white px-4 py-3 text-sm text-[#111827] outline-none transition-colors focus:border-[#111827]" />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-[#111827]">副标题</label>
                  <textarea value={subtitle} onChange={(e) => setSubtitle(e.target.value)} rows={3} className="w-full rounded-xl border border-[#E5E7EB] bg-white px-4 py-3 text-sm text-[#111827] outline-none transition-colors focus:border-[#111827]" />
                </div>
              </div>

              <div className="grid gap-5 md:grid-cols-3">
                <div className="rounded-2xl border border-[#E5E7EB] p-4 md:col-span-3">
                  <div className="flex items-center gap-2 text-sm font-medium text-[#111827]"><Hash className="h-4 w-4" />标签</div>
                  <input value={tags} onChange={(e) => setTags(e.target.value)} className="mt-3 w-full rounded-xl border border-[#E5E7EB] bg-white px-4 py-3 text-sm text-[#111827] outline-none transition-colors focus:border-[#111827]" placeholder="用逗号分隔标签" />
                  <p className="mt-2 text-xs text-[#6B7280]">示例：量子计算, 纠错码</p>
                </div>
              </div>

              {/* 公众号文章编辑器：单篇正文 */}
              <div className="rounded-2xl border border-[#E5E7EB] bg-white">
                <div className="flex flex-wrap items-center gap-2 border-b border-[#E5E7EB] px-4 py-3">
                  <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#6B7280]">正文</span>
                  <button
                    type="button"
                    onClick={() => imgInputRef.current?.click()}
                    className="inline-flex items-center gap-2 rounded-lg border border-[#E5E7EB] bg-white px-3 py-1.5 text-xs font-medium text-[#111827] transition-colors hover:bg-[#F9FAFB]"
                  >
                    <ImageIcon className="h-3.5 w-3.5" />
                    插入图片
                  </button>
                  <input
                    ref={imgInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => void handlePickImage(e.target.files?.[0] ?? null)}
                  />
                  <span className="ml-auto text-xs text-[#6B7280]">
                    {stats.words.toLocaleString()} 字 · {stats.paras} 段 · {stats.headings} 标题
                  </span>
                </div>
                <div className="p-4">
                  <div
                    ref={editorRef}
                    contentEditable
                    suppressContentEditableWarning
                    onInput={syncFromDom}
                    onBlur={syncFromDom}
                    data-placeholder="开始写作…（可直接粘贴文字与插入图片）"
                    className="min-h-[420px] w-full rounded-xl border border-[#E5E7EB] bg-white px-4 py-3 text-sm leading-7 text-[#111827] outline-none transition-colors focus-within:border-[#111827]"
                    style={{ whiteSpace: "pre-wrap" }}
                  />
                  <div className="mt-2 text-xs text-[#6B7280]">
                    提示：这是所见即所得编辑区；插入图片会直接显示在正文里。右侧信息会自动联动统计。
                  </div>
                </div>
              </div>
            </section>

            <aside className="space-y-4 lg:sticky lg:top-6 lg:h-fit">
              <div className="rounded-2xl border border-[#E5E7EB] p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#6B7280]">文档信息</p>
                <div className="mt-4 space-y-3 text-sm text-[#4B5563]">
                  <div className="flex items-center justify-between gap-3"><span className="text-[#6B7280]">更新时间</span><span>{new Date().toISOString().slice(0, 10)}</span></div>
                  <div className="flex items-center justify-between gap-3"><span className="text-[#6B7280]">字数</span><span>{stats.words.toLocaleString()} 字</span></div>
                  <div className="flex items-center justify-between gap-3"><span className="text-[#6B7280]">段落</span><span>{stats.paras}</span></div>
                  <div className="flex items-center justify-between gap-3"><span className="text-[#6B7280]">标题</span><span>{stats.headings}</span></div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>
    </div>
  );
}

type MdBlock =
  | { kind: "h2"; text: string }
  | { kind: "h3"; text: string }
  | { kind: "p"; text: string }
  | { kind: "img"; alt: string; src: string };

function markdownBlocks(content: string): MdBlock[] {
  const lines = (content || "").split("\n");
  const blocks: MdBlock[] = [];
  let buf: string[] = [];
  const flush = () => {
    const txt = buf.join("\n").trim();
    buf = [];
    if (!txt) return;
    blocks.push({ kind: "p", text: txt });
  };

  for (const raw of lines) {
    const line = raw ?? "";
    const trimmed = line.trim();
    const img = trimmed.match(/^!\[([^\]]*)\]\(([^)]+)\)\s*$/);
    if (img) {
      flush();
      blocks.push({ kind: "img", alt: img[1] || "", src: img[2] });
      continue;
    }
    const h2 = trimmed.match(/^##\s+(.+)$/);
    if (h2) {
      flush();
      blocks.push({ kind: "h2", text: h2[1] });
      continue;
    }
    const h3 = trimmed.match(/^###\s+(.+)$/);
    if (h3) {
      flush();
      blocks.push({ kind: "h3", text: h3[1] });
      continue;
    }
    if (!trimmed) {
      flush();
      continue;
    }
    buf.push(line);
  }
  flush();
  return blocks;
}

function blocksToHtml(blocks: MdBlock[]): string {
  const esc = (s: string) =>
    s
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  const out: string[] = [];
  for (const b of blocks) {
    if (b.kind === "h2") out.push(`<h2>${esc(b.text)}</h2>`);
    else if (b.kind === "h3") out.push(`<h3>${esc(b.text)}</h3>`);
    else if (b.kind === "img") out.push(`<img src="${esc(b.src)}" alt="${esc(b.alt)}" />`);
    else out.push(`<p>${esc(b.text).replace(/\n/g, "<br/>")}</p>`);
  }
  return out.join("");
}

function stripHtml(html: string): string {
  if (!html) return "";
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return (tmp.textContent || tmp.innerText || "").replace(/\u00a0/g, " ");
}

function countHeadingsFromHtml(html: string): number {
  if (!html) return 0;
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return tmp.querySelectorAll("h2, h3").length;
}
